import { useState } from "react";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

// Group picker that is extensible: pick an existing group (canonical GROUPS ∪
// groups already in use) or type a new name and create it on the spot — the
// server stores group as a plain string, so no registry to maintain.
export const GroupCombobox = ({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const search = trimmed.toLowerCase();
  // Keep a newly chosen group available until the app is saved/refetched.
  const groups = [...new Set([...options, value].filter(Boolean))];
  const suggestions = groups.filter((g) => g.toLowerCase().includes(search));
  const canCreate =
    !!trimmed && !groups.some((g) => g.toLowerCase() === search);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-label="Group"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          {value || "-"}
          <ChevronsUpDownIcon className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
      >
        {/* Filter normalized names ourselves so cmdk cannot hide Create. */}
        <Command shouldFilter={false} label="Group">
          <CommandInput
            placeholder="Search or type a new group…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandGroup>
              {!trimmed && (
                <CommandItem value="__none__" onSelect={() => pick("")}>
                  - (no group)
                  {value === "" && <CheckIcon className="ml-auto h-4 w-4" />}
                </CommandItem>
              )}
              {suggestions.map((g) => (
                <CommandItem key={g} value={`group ${g}`} onSelect={() => pick(g)}>
                  {g}
                  {value === g && <CheckIcon className="ml-auto h-4 w-4" />}
                </CommandItem>
              ))}
              {canCreate && (
                <CommandItem
                  value={`create ${trimmed}`}
                  onSelect={() => pick(trimmed)}
                >
                  Create “{trimmed}”
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
